import { __assign, __rest, __spreadArray, __read } from 'tslib';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { forwardRef } from 'react';
import FocusLock from 'react-focus-lock';
import { CalendarOutlineIcon } from '@compono/ui-icons';
import { TextInput } from '../TextInput/TextInput.js';
import { IconBox } from '../IconBox/index.js';
import { Label } from '../Label/index.js';
import { Grid } from '../Grid/index.js';
import '../hooks/useDetectInputMethod.js';
import '@reach/auto-id';
import '@emotion/react';
import 'lodash.get';
import '@compono/ui-theme';
import 'deepmerge';
import '@emotion/is-prop-valid';
import { useUpdateEffect } from '../hooks/useUpdateEffect/index.js';
import { DatePickerMenu } from '../DatePickerMenu/DatePickerMenu.js';
import { DatePickerMenuContent } from '../DatePickerMenu/DatePickerMenuContent.js';
import { useDatePickerRange } from '../DatePicker/hooks/useDatePickerRange.js';
import { useDatePickerRangeInput, convertDateToIso, convertDateToInput } from '../DatePicker/hooks/useDatePickerRangeInput.js';
import { DatePickerRangeProvider } from './DatePickerRangeContext.js';
import isSameDay from '../node_modules/date-fns/esm/isSameDay/index.js';
import isBefore from '../node_modules/date-fns/esm/isBefore/index.js';

var DatePickerRangeInputIcon = function (_a) {
    var id = _a.id;
    return (jsx(Label, __assign({ htmlFor: id, sx: { lineHeight: 0 }, "data-date-picker-range-input-label": true }, { children: jsx(IconBox, { position: "left", icon: CalendarOutlineIcon, themeKey: "inputIcons", title: "Select a date", "data-date-picker-range-input-icon": true }) })));
};
var DatePickerRangeStartInput = forwardRef(function (props, ref) {
    var inputProps = useDatePickerRangeInput(__assign(__assign({}, props), { ref: ref, type: "start" }));
    return (jsx(TextInput, __assign({ iconLeft: jsx(DatePickerRangeInputIcon, { id: inputProps.id }) }, inputProps)));
});
var DatePickerRangeEndInput = forwardRef(function (props, ref) {
    var inputProps = useDatePickerRangeInput(__assign(__assign({}, props), { ref: ref, type: "end" }));
    return (jsx(TextInput, __assign({ iconLeft: jsx(DatePickerRangeInputIcon, { id: inputProps.id }) }, inputProps)));
});
var DatePickerRangeInner = function (_a) {
    var children = _a.children, size = _a.size, inputFormat = _a.inputFormat, dismissOnSelect = _a.dismissOnSelect, contentProps = _a.contentProps, focusLockProps = _a.focusLockProps, gridProps = _a.gridProps;
    var rangeContext = useDatePickerRange({
        inputFormat: inputFormat,
        size: size,
        dismissOnSelect: dismissOnSelect,
    });
    var startDateValue = rangeContext.startDateValue, endDateValue = rangeContext.endDateValue, setStartInputValue = rangeContext.setStartInputValue, setStartDateValue = rangeContext.setStartDateValue, setEndInputValue = rangeContext.setEndInputValue, setEndDateValue = rangeContext.setEndDateValue, onStartChange = rangeContext.startInputProps.onChange, onEndChange = rangeContext.endInputProps.onChange, focusDateRef = rangeContext.focusDateRef, startInputRef = rangeContext.startInputRef, endInputRef = rangeContext.endInputRef, isOpen = rangeContext.isOpen, openedBy = rangeContext.openedBy, id = rangeContext.id, setOpenedBy = rangeContext.setOpenedBy, setIsOpen = rangeContext.setIsOpen;
    var isOpenedByStart = openedBy === "start";
    var selectHandler = function (_a) {
        var date = _a.date;
        if (!openedBy)
            return;
        var dateValue = isOpenedByStart ? startDateValue : endDateValue;
        var setInputValue = isOpenedByStart
            ? setStartInputValue
            : setEndInputValue;
        var setDateValue = isOpenedByStart ? setStartDateValue : setEndDateValue;
        var onChange = isOpenedByStart ? onStartChange : onEndChange;
        if (!(dateValue && isSameDay(date, dateValue))) {
            var value = convertDateToIso(date);
            var inputValue = convertDateToInput(date, inputFormat);
            setInputValue && setInputValue(inputValue);
            setDateValue && setDateValue(date);
            onChange && onChange(value);
            isOpenedByStart && !endDateValue && setOpenedBy("end");
            !isOpenedByStart && !startDateValue && setOpenedBy("start");
        }
        isOpenedByStart && date && endDateValue && setIsOpen(false);
        !isOpenedByStart && date && startDateValue && setIsOpen(false);
    };
    var getDatePickerValue = function (startDateVal, endDateVal) {
        if (endDateVal && startDateVal && isBefore(endDateVal, startDateVal)) {
            return [];
        }
        return __spreadArray(__spreadArray([], __read((startDateVal ? [startDateVal] : [])), false), __read((endDateVal ? [endDateVal] : [])), false);
    };
    var getDisabledBefore = function (startDateVal, endDateVal) {
        if (!startDateVal && !endDateVal)
            return undefined;
        if (startDateVal && !endDateVal && isOpenedByStart)
            return undefined;
        if (startDateVal && endDateVal && !isOpenedByStart) {
            return startDateVal;
        }
        if (startDateVal && !endDateVal) {
            return startDateVal;
        }
    };
    var getDisabledAfter = function (startDateVal, endDateVal) {
        if (!startDateVal && !endDateVal)
            return undefined;
        if (!startDateVal && endDateVal && !isOpenedByStart)
            return undefined;
        if (startDateVal && endDateVal && isOpenedByStart) {
            return endDateVal;
        }
        if (endDateVal && !startDateVal) {
            return endDateVal;
        }
    };
    var getShowRange = function (startDateVal, endDateVal) {
        if (!startDateVal && endDateVal && !isOpenedByStart)
            return undefined;
        if (startDateVal && !endDateVal && isOpenedByStart)
            return undefined;
        return true;
    };
    useUpdateEffect(function () {
        var _a, _b;
        if (!isOpen) {
            setOpenedBy(undefined);
            if (!isOpenedByStart) {
                (_a = endInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
            if (isOpenedByStart) {
                (_b = startInputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
        }
    }, [isOpen]);
    return (jsxs(DatePickerRangeProvider, __assign({ value: rangeContext }, { children: [jsx(FocusLock, __assign({}, __assign({ autoFocus: false, group: "focus-lock-group--".concat(id), disabled: !isOpen }, focusLockProps), { children: jsx(Grid, __assign({ "data-date-picker-range-grid": true, themeKey: "datePickerRangeGrid" }, gridProps, { children: children })) })), jsx(DatePickerMenuContent, __assign({}, __assign({ value: getDatePickerValue(startDateValue, endDateValue), disabledAfter: getDisabledAfter(startDateValue, endDateValue), disabledBefore: getDisabledBefore(startDateValue, endDateValue), onSelect: selectHandler, focusDateRef: focusDateRef, autoFocus: false, size: size, dropdownContentProp: {
                    focusLockProps: {
                        autoFocus: false,
                        group: "focus-lock-group--".concat(id),
                    },
                }, showRange: getShowRange(startDateValue, endDateValue), monthNum: 2 }, contentProps)))] })));
};
var DatePickerRange = function (_a) {
    var menuProps = _a.menuProps, _b = _a.size, size = _b === void 0 ? "md" : _b, props = __rest(_a, ["menuProps", "size"]);
    return (jsx(DatePickerMenu, __assign({}, menuProps, { children: jsx(DatePickerRangeInner, __assign({ size: size }, props)) })));
};

export { DatePickerRange, DatePickerRangeEndInput, DatePickerRangeStartInput };
//# sourceMappingURL=DatePickerRange.js.map
